import Medusa from "@medusajs/js-sdk"

export const backendUrl = __BACKEND_URL__ ?? "/"
export const publishableApiKey = __PUBLISHABLE_API_KEY__ ?? ""

const token = window.localStorage.getItem("medusa_auth_token") || ""

const decodeJwt = (token: string) => {
  try {
    const payload = token.split(".")[1]

    return JSON.parse(atob(payload))
  } catch (err) {
    return null
  }
}

const isTokenExpired = (token: string | null) => {
  if (!token) return true

  const payload = decodeJwt(token)
  if (!payload?.exp) return true

  return payload.exp * 1000 < Date.now()
}

export const sdk = new Medusa({
  baseUrl: backendUrl,
  publishableKey: publishableApiKey,
  auth: {
    type: "jwt",
  },
})

// useful when you want to call the BE from the console and try things out quickly
if (typeof window !== "undefined") {
  ;(window as any).__sdk = sdk
}

export const importProductsQuery = async (file: File) => {
  const formData = new FormData()
  formData.append("file", file)

  return await fetch(`${backendUrl}/vendor/products/import`, {
    method: "POST",
    body: formData,
    headers: {
      authorization: `Bearer ${token}`,
      "x-publishable-api-key": publishableApiKey,
    },
  })
    .then((res) => res.json())
    .catch(() => null)
}

export const uploadFilesQuery = async (files: any[]) => {
  const formData = new FormData()

  for (const { file } of files) {
    formData.append("files", file)
  }

  return await fetch(`${backendUrl}/vendor/uploads`, {
    method: "POST",
    body: formData,
    headers: {
      authorization: `Bearer ${token}`,
      "x-publishable-api-key": publishableApiKey,
    },
  })
    .then((res) => res.json())
    .catch(() => null)
}

export const fetchQuery = async (
  url: string,
  {
    method,
    body,
    query,
    headers,
    config,
  }: {
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
    body?: BodyInit | object | string | null
    query?: Record<string, string | number>
    headers?: { [key: string]: string }
    config?: { [key: string]: any }
  }
) => {
  const bearer = (await window.localStorage.getItem("medusa_auth_token")) || ""
  const params = Object.entries(query || {}).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      if (Array.isArray(value)) {
        // Send arrays as multiple query parameters with bracket notation
        // This allows backends to parse them as arrays: status[]=draft&status[]=published
        const arrayParams = value
          .map(
            (item) => `${encodeURIComponent(key)}[]=${encodeURIComponent(item)}`
          )
          .join("&")
        if (acc) {
          acc += "&" + arrayParams
        } else {
          acc = arrayParams
        }
      } else {
        const separator = acc ? "&" : ""
        const serializedValue =
          typeof value === "object" ? JSON.stringify(value) : value
        acc += `${separator}${encodeURIComponent(key)}=${encodeURIComponent(serializedValue)}`
      }
      return acc
    }
    return acc
  }, "")
  const response = await fetch(`${backendUrl}${url}${params && `?${params}`}`, {
    method: method,
    headers: {
      authorization: `Bearer ${bearer}`,
      "Content-Type": "application/json",
      "x-publishable-api-key": publishableApiKey,
      ...headers,
      ...(config?.headers ?? {}),
    },
    body: body ? JSON.stringify(body) : null,
    ...config,
  })

  // if (!response.ok) {
  //   const errorData = await response.json()

  //   if (response.status === 401) {
  //     if (isTokenExpired(token)) {
  //       localStorage.removeItem("medusa_auth_token")
  //       window.location.href = "/login?reason=Unauthorized"
  //       return
  //     }

  //     throw {
  //       type: "NO_PERMISSION",
  //       message: errorData.message || "Unauthorized",
  //     }
  //   }

  //   const error = new Error(errorData.message || "Server error")
  //   ;(error as Error & { status: number }).status = response.status
  //   throw error
  // }

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)

    if (response.status === 401) {
      if (isTokenExpired(token)) {
        localStorage.removeItem("medusa_auth_token")
        window.location.href = "/login?reason=Unauthorized"
        return
      }

      throw {
        type: "NO_PERMISSION",
        message:
          errorData?.system?.message || errorData?.message || "Unauthorized",
        response: {
          data: errorData,
          status: response.status,
        },
      }
    }

    throw {
      message:
        errorData?.system?.message || errorData?.message || "Server error",
      response: {
        data: errorData,
        status: response.status,
      },
    }
  }

  return response.json()
}
